import { useContext, useEffect, useState } from 'react';
import { Stack, TextField } from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { SuperAdminSurveyTemplateService } from '../../../shared/services/survey-template/survey-template.service';
import { ISurveyTemplate } from '../../../shared/services/survey-template/survey-template.model';

interface Props {
  open: boolean;
  onClose: () => void;
  // Receives the template id on success (the new one when creating, the same one when editing).
  onSuccess: (templateId: string) => void;
  // When provided, the dialog edits that template's name/description instead of creating one.
  existing?: ISurveyTemplate;
}

// Creates the empty template shell, or edits an existing template's name and description. A
// template's questions and rules are authored on the detail page with the shared builder dialogs.
export const SuperAdminSurveyTemplateCreateDialog = ({ open, onClose, onSuccess, existing }: Props) => {
  const service = new SuperAdminSurveyTemplateService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isEdit = !!existing;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(existing?.name ?? '');
    setDescription(existing?.description ?? '');
  }, [open, existing]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload = { name: name.trim(), description: description.trim() || null };
      const saved = isEdit
        ? await service.edit(existing!.id, payload)
        : await service.create(payload);
      userNotificationsContext.openSnackbar({ open: true, message: isEdit ? 'Template updated' : 'Template created', backgroundColor: 'primary.dark' });
      onSuccess(saved.id);
      onClose();
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'An error occurred, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Template Details' : 'Create Survey Template'}
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField label='Template name' value={name} onChange={e => setName(e.target.value)} fullWidth required />
        <TextField label='Description (optional)' value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline rows={2} />
      </Stack>
    </DialogWrapper>
  );
};
